import { useQueryParams } from '@calendly/hooks'
import { httpRequest } from '@calendly/http-tools'
import { useSelector } from 'react-redux'
import { useCallback } from 'react'

import { isCookiesAllowedSelector } from 'selectors/gdpr'

/**
 * @description This hook is used to track analytics events.
 * This should follow the general schema for sendTrackEvent outlined here
 * engines/backend/api_docs/reference/analytics-asyncapi.yaml
 * Which should be kept in sync with
 * https://github:com/calendly/analytics-service/blob/main/reference/analytics-asyncapi.yaml
 *
 * The backend is responsible for appending browser, request, and timestamp information to the event.
 * The following properties are required (see schema for future reference):
 * - event_name: string  (Shorthand: name)
 * - event_payload: object (Shorthand: properties, default: {})
 * - event_version: number (Shorthand: version, default: 1)
 * - dependencies: object (Shorthand: dependencies, default: {})
 *
 * This hook will attempt to get the anonymousId from the Segment SDK and append it to the event.
 * This approach works even when cookies are blocked, unlike reading the ajs_anonymous_id cookie
 * directly. For tracking anonymous users, do not pass user_uuid as a dependency.
 *
 * This hook will attempt to respect oneTrust GDPR cookie settings for 'performance' group by default,
 * and will not track if the booking page is in preview mode.
 *
 * @param {string} categoryName - The name of the oneTrust group to check for GDPR compliance.
 * @returns {
 *  Function(name: string, properties: object, dependencies: object, version: number): Promise<void>
 * } track function that takes an analyticsEvent object.
 */

export const useTrackCommerce = (categoryName = 'performance') => {
  const { embed_type: embedType } = useQueryParams()
  const allowed = useSelector(isCookiesAllowedSelector(categoryName)) && embedType !== 'preview'

  const track = useCallback(async (name, properties = {}, dependencies = {}, version = 1) => {
    if (allowed && window.analytics) {
      const hasAnonymousId = typeof window.analytics.user === 'function'
        && typeof window.analytics.user().anonymousId === 'function'
      const anonymousId = hasAnonymousId ? window.analytics.user().anonymousId() : undefined

      const [success, response] = await httpRequest({
        url: '/api/booking/analytics_commerce/track',
        method: 'POST',
        data: {
          event_name: name,
          event_payload: properties,
          event_version: version,
          anonymous_id: anonymousId,
          dependencies,
        }
      })

      if (!success && (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'review')) {
        console.error(`Dispatching ${name} failed.\n\n${response?.payload?.message}`)
      }

      return success
    }

    return false
  }, [allowed])

  return track
}
